import { useState, useRef } from 'react';
import { useRSVP } from '../../context/RSVPContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { UserRound, Camera, Upload, ArrowRight, Loader2 } from 'lucide-react';
import { useGuestAvatarUpload } from '../../hooks/useGuestAvatarUpload';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useIsMobile } from '@/components/profile/hooks/useIsMobile';
import { RSVPButton } from '@/components/ui/rsvp-button';
export const SelfieStep = () => {
  const {
    selectedGuest,
    eventId,
    guestAvatarUrl,
    setGuestAvatarUrl,
    goToNextStep,
    goToPreviousStep,
    submitRSVP,
    isSubmitting
  } = useRSVP();
  const isMobile = useIsMobile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitError, setIsSubmitError] = useState(false);

  // If no guest is selected, we shouldn't be on this step
  if (!selectedGuest || !eventId) {
    return <div className="text-center p-8">
        <p className="text-muted-foreground">No guest selected</p>
        <Button onClick={goToNextStep} className="mt-4">Continue</Button>
      </div>;
  }
  const {
    isUploading,
    handleImageUpload,
    handleSkipSelfie
  } = useGuestAvatarUpload({
    inviteId: selectedGuest.inviteId,
    eventId,
    onAvatarChange: setGuestAvatarUrl
  });
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };
  const handleSubmitRSVP = async () => {
    setIsSubmitError(false);
    const success = await submitRSVP();
    if (success) {
      console.log('RSVP submission successful, moving to next step');
      goToNextStep();
    } else {
      console.log('RSVP submission failed');
      setIsSubmitError(true);
    }
  };
  return <div className="flex flex-col h-full justify-between">
      <div className="flex-1 flex flex-col">
        <div className="h-[30vh] min-h-[120px] max-h-[180px]"></div>
        
        <div className="px-4 md:px-6">
          <div className="max-w-md mx-auto w-full text-center">
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-semibold">Add a selfie to your RSVP</h2>
                <p className="text-muted-foreground text-xs">(optional)</p>
              </div>

              <div className="flex flex-col items-center space-y-4">
                {/* Avatar with loading overlay */}
                <div className="relative">
                  <Avatar className="h-24 w-24 overflow-hidden">
                    {guestAvatarUrl ? <AvatarImage src={guestAvatarUrl} alt={selectedGuest.displayName} /> : null}
                    <AvatarFallback className="text-2xl">
                      <UserRound className="h-12 w-12" />
                    </AvatarFallback>
                  </Avatar>
                  
                  {/* Loading overlay for avatar */}
                  {isUploading && <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-full">
                      <Loader2 className="h-8 w-8 text-white animate-spin" />
                    </div>}
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex items-center gap-1" disabled={isUploading || isSubmitting} onClick={handleUploadClick}>
                    {isUploading ? <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Uploading...
                      </> : <>
                        <Upload className="h-4 w-4" />
                        Upload
                      </>}
                  </Button>
                  <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="selfie-upload" disabled={isUploading || isSubmitting} ref={fileInputRef} />
                  
                  {isMobile && <>
                      <Button variant="outline" size="sm" className="flex items-center gap-1" disabled={isUploading || isSubmitting} onClick={handleCameraClick}>
                        {isUploading ? <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Uploading...
                          </> : <>
                            <Camera className="h-4 w-4" />
                            Camera
                          </>}
                      </Button>
                      <input type="file" accept="image/*" capture="user" onChange={handleFileChange} className="hidden" id="selfie-camera" disabled={isUploading || isSubmitting} ref={cameraInputRef} />
                    </>}
                </div>
              </div>

              {isSubmitError && <p className="text-destructive text-sm text-center">
                  There was an error submitting your RSVP. Please try again.
                </p>}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed footer with buttons, matching other RSVP steps */}
      <div className="fixed bottom-0 left-0 right-0 p-4 pb-12 bg-white">
        <div className="max-w-2xl mx-auto px-4 flex flex-col items-center">
          <RSVPButton onClick={handleSubmitRSVP} disabled={isUploading || isSubmitting} className="mx-auto block">
            {isSubmitting ? <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </> : 'Send RSVP'}
          </RSVPButton>
          
          <Button variant="link" className="mt-2 text-sm text-muted-foreground" onClick={goToPreviousStep} disabled={isUploading || isSubmitting}>
            Back to feedback
          </Button>
        </div>
      </div>
    </div>;
};